import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Typography, IconButton, Slider } from '@mui/material';
import { 
  PlayArrow, 
  Pause, 
  Download as DownloadIcon,
  VolumeUp 
} from '@mui/icons-material';
import { MessageProps } from '../../../types/chat';
import MessageBubble from './MessageBubble';

const AudioMessage: React.FC<MessageProps> = React.memo(({ 
  message, 
  isCurrentUser,
  onReactionPress,
  onReplyPress,
  onDeletePress,
  currentUserId,
  currentUserType,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDurationLoaded, setIsDurationLoaded] = useState(false);

  const handleReactionPress = (emoji: string) => {
    onReactionPress?.(message, emoji);
  };

  // Helper function to parse duration strings like "1:23" or "83 seconds"
  const parseDurationString = (durationStr: string): number => {
    if (!durationStr) return 0;
    
    // Try MM:SS format first (e.g., "1:23")
    const timeMatch = durationStr.match(/^(\d+):(\d+)$/);
    if (timeMatch) {
      const minutes = parseInt(timeMatch[1], 10);
      const seconds = parseInt(timeMatch[2], 10);
      return minutes * 60 + seconds;
    }
    
    // Try number format (e.g., "83 seconds" or just "83")
    const numMatch = durationStr.match(/^(\d+)/);
    if (numMatch) {
      return parseInt(numMatch[1], 10);
    }
    
    return 0;
  };

  const handlePlayPause = useCallback(() => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch((err) => {
        console.error('Failed to play audio:', err);
        setError('Failed to play audio');
      });
    }
  }, [isPlaying]);

  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      const audioDuration = audioRef.current.duration;
      
      // Check if duration is valid and finite
      if (isFinite(audioDuration) && audioDuration > 0) {
        setDuration(audioDuration);
        setIsDurationLoaded(true);
        setError(null); // Clear any previous errors
      } else {
        // Fallback: try to parse message.duration if available
        if (message.duration) {
          // Attempt to parse common duration formats like "1:23" or "83 seconds"
          const parsed = parseDurationString(message.duration);
          if (parsed > 0) {
            setDuration(parsed);
            setIsDurationLoaded(true);
          }
        }
      }
    }
  }, [message.duration]);

  const handleSeek = useCallback((value: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value;
      setCurrentTime(value);
    }
  }, []);

  const handleDownload = useCallback(async () => {
    if (!message.audioUrl) return;
    
    try {
      const response = await fetch(message.audioUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audio-${message.id}.mp3`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download audio:', error);
    }
  }, [message.audioUrl, message.id]);

  const handleAudioError = useCallback((event: any) => {
    console.error('Audio error details:', {
      error: event.target?.error,
      code: event.target?.error?.code,
      message: event.target?.error?.message,
      audioUrl: message.audioUrl,
      networkState: event.target?.networkState,
      readyState: event.target?.readyState
    });
    setError('Failed to load audio');
  }, [message.audioUrl]);

  const formatTime = useCallback((seconds: number) => {
    if (isNaN(seconds) || !isFinite(seconds)) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handlePlay = () => {
      setIsPlaying(true);
    };
    const handlePause = () => {
      setIsPlaying(false);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleAudioError);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleAudioError);
    };
  }, [handleTimeUpdate, handleLoadedMetadata, handleAudioError]);

  // Add timeout to detect if metadata never loads
  useEffect(() => {
    if (!isDurationLoaded && message.audioUrl) {
      const timeout = setTimeout(() => {
        // If metadata hasn't loaded after 5 seconds, try to use message.duration
        if (!isDurationLoaded && message.duration) {
          const parsed = parseDurationString(message.duration);
          if (parsed > 0) {
            setDuration(parsed);
            setIsDurationLoaded(true);
          }
        }
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [isDurationLoaded, message.audioUrl, message.duration]);

  if (!message.audioUrl) {
    return (
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        currentUserId={currentUserId}
      >
        <Box sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
          Audio not available
        </Box>
      </MessageBubble>
    );
  }

  if (error) {
    return (
      <MessageBubble 
        message={message} 
        isCurrentUser={isCurrentUser}
        onReactionPress={handleReactionPress}
        onReplyPress={onReplyPress}
        onDeletePress={onDeletePress}
        currentUserId={currentUserId}
        currentUserType={currentUserType}
      >
        <Box sx={{ 
          p: 2,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          textAlign: 'center',
          color: 'text.secondary'
        }}>
          <Typography variant="body2">
            {error}
          </Typography>
        </Box>
      </MessageBubble>
    );
  }

  return (
    <MessageBubble 
      message={message} 
      isCurrentUser={isCurrentUser}
      onReactionPress={handleReactionPress}
      onReplyPress={onReplyPress}
      onDeletePress={onDeletePress}
      currentUserId={currentUserId}
      currentUserType={currentUserType}
    >
      <Box sx={{ minWidth: 250, maxWidth: 350 }}>
        {/* Hidden audio element */}
        <audio ref={audioRef} src={message.audioUrl} preload="metadata" />
        
        {/* Audio player UI */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          p: 1,
          mt: 1,
          backgroundColor: isCurrentUser ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          borderRadius: 1
        }}>
          {/* Play/Pause button */}
          <IconButton 
            onClick={handlePlayPause}
            size="small"
            sx={{ 
              color: isCurrentUser ? 'white' : 'primary.main',
              '&:hover': {
                backgroundColor: isCurrentUser ? 'rgba(255,255,255,0.1)' : 'action.hover'
              }
            }}
          >
            {isPlaying ? <Pause /> : <PlayArrow />}
          </IconButton>
          
          {/* Waveform placeholder / Progress slider */}
          <Box sx={{ flex: 1, mx: 1 }}>
            <Slider
              size="small"
              value={currentTime}
              max={duration}
              onChange={(_, value) => handleSeek(value as number)}
              sx={{
                color: isCurrentUser ? 'rgba(255,255,255,0.8)' : 'primary.main',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                },
                '& .MuiSlider-track': {
                  height: 3,
                },
                '& .MuiSlider-rail': {
                  height: 3,
                  opacity: 0.3,
                },
              }}
            />
          </Box>
          
          {/* Duration */}
          <Typography 
            variant="caption" 
            sx={{ 
              color: isCurrentUser ? 'rgba(255,255,255,0.8)' : 'text.secondary',
              minWidth: 60,
              textAlign: 'center',
              fontSize: '12px'
            }}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </Typography>
          
          {/* Download button */}
          <IconButton 
            onClick={handleDownload}
            size="small"
            sx={{ 
              color: isCurrentUser ? 'rgba(255,255,255,0.8)' : 'text.secondary',
              '&:hover': {
                backgroundColor: isCurrentUser ? 'rgba(255,255,255,0.1)' : 'action.hover'
              }
            }}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Audio info */}
        <Box sx={{ 
          mt: 1, 
          display: 'flex', 
          alignItems: 'center',
          gap: 1
        }}>
          <VolumeUp 
            fontSize="small" 
            sx={{ 
              color: isCurrentUser ? 'rgba(255,255,255,0.6)' : 'text.secondary',
              opacity: 0.7 
            }} 
          />
          <Typography 
            variant="caption" 
            sx={{ 
              opacity: 0.7,
              color: isCurrentUser ? 'rgba(255,255,255,0.8)' : 'text.secondary'
            }}
          >
            {message.duration || 'Audio Message'}
          </Typography>
        </Box>
        
        {/* Optional message text with audio */}
        {message.message && message.message.trim() && (
          <Box sx={{ mt: 1, fontSize: '14px', lineHeight: 1.4 }}>
            {message.message}
          </Box>
        )}
      </Box>
    </MessageBubble>
  );
});

AudioMessage.displayName = 'AudioMessage';

export default AudioMessage;
